import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../Components/PageHeader/PageHeader';
import AppTable from '../../Components/AppTable/AppTable';
import { useDispatch, useSelector } from 'react-redux';
import { ApiHit } from '../../Api/ApiHit';
import { searchCashRecord, deleteCashRecord, dailyStatsCashRecord } from '../../Constant/Constant';
import { setApiJson } from '../../Redux/Actions/ApiAction';
import { setPagination } from '../../Redux/Actions/TableAction';
import ActionButton from '../../Components/AppButton/ActionButton';
import DeleteActionButton from '../../Components/AppButton/DeleteActionButton';
import { FileText, PencilIcon, DollarSign, TrendingUp, TrendingDown, BarChart3, IndianRupee } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Button } from 'rizzui/button';
import { CircleMinus, CirclePlus } from 'lucide-react';
import { getCahbookUser, getSelectedAccount, getSelectedBook } from '../../Storage/Storage';
import { hasPermission, formatDate, exportToCSV, exportToPDF } from '../../utils';
import UserMultiSelect from './UserMultiSelect';
import { dashboardOptions } from '../../Constant/Options';
import DateSearch from '../../Components/DateRangeFilter/DateSearch';
import { CalendarRange } from 'lucide-react';
import FilterSection from './FilterSection';
import ProofGalleryModal from '../../Components/ProofGallery/ProofGalleryModal';

function CashbookManagement() {
  const TableReducer = useSelector((state) => state.TableReducer);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [dateRangeMode, setDateRangeMode] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    cashMethod: '',
    paymentMethod: ''
  });

  // Proof gallery modal state
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const openGallery = (urls) => {
    setGalleryUrls(urls);
    setGalleryOpen(true);
  };

  // Payment methods
  const paymentMethods = ['Cash', 'UPI', 'Bank Transfer', 'Card', 'Cheque'];

  // Dashboard stats and user dropdown states


  const convertDateToTimestamp = (dateString, isStartOfDay = true) => {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isStartOfDay) {
      return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
    } else {
      return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
    }
  };


  const getDateRange = useCallback((period) => {
    // If no period selected or empty, return null (show all records)
    if (!period || period === '') {
      console.log('No period selected - showing all records');
      return null;
    }

    // Handle custom date range
    if (period === 'daterange') {
      // If dates are selected, use them
      if (startDate && endDate) {
        console.log('Using custom date range:', startDate, 'to', endDate);
        return {
          start: convertDateToTimestamp(startDate, true),
          end: convertDateToTimestamp(endDate, false)
        };
      }
      // If date range is selected but no dates provided, return null (no filter)
      console.log('Date range mode but no dates selected - showing all records');
      return null;
    }

    const now = new Date();
    let startTimestamp = new Date();
    let endTimestamp = new Date();

    switch (period) {
      case 'today':
        startTimestamp.setHours(0, 0, 0, 0);
        endTimestamp.setHours(23, 59, 59, 999);
        break;
      case 'currentWeek':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        startTimestamp = startOfWeek;
        endTimestamp = new Date();
        break;
      case 'currentMonth':
        startTimestamp = new Date(now.getFullYear(), now.getMonth(), 1);
        endTimestamp = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last7Days':
        startTimestamp = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endTimestamp = new Date();
        break;
      case 'last30Days':
        startTimestamp = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endTimestamp = new Date();
        break;
      default:
        console.log('Unknown period - showing all records');
        return null;
    }

    console.log('Using predefined period:', period, 'from', new Date(startTimestamp.getTime()), 'to', new Date(endTimestamp.getTime()));
    return {
      start: startTimestamp.getTime(),
      end: endTimestamp.getTime()
    };
  }, [startDate, endDate]);

  const [statsData, setStatsData] = useState({
    totalRecords: 0,
    totalIn: 0,
    totalOut: 0,
    netBalance: 0,
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  // Permissions
  const canAdd = hasPermission('cashbook', 'read');
  const canRead = hasPermission('cashbook', 'read');
  const canEdit = hasPermission('cashbook', 'write');
  const canDelete = hasPermission('cashbook', 'delete');

  // Check if user is SuperAdmin
  const currentUser = getCahbookUser();
  const isSuperAdmin = currentUser?.roleId?.roleName === 'SuperAdmin';
  const isAdmin = currentUser?.roleId?.roleName === 'Admin';
  const isAccountant = currentUser?.roleId?.roleName === 'Accountant';



  // Fetch data
  useEffect(() => {
    console.log('🎯 useEffect triggered - Dependencies changed:', {
      page: TableReducer.pagination.page,
      limit: TableReducer.pagination.limit,
      canRead,
      selectedUsers: selectedUsers.length,
      selectedPeriod,
      startDate,
      endDate,
      filters
    });

    if (canRead) {
      getCashbookRecords();
      fetchDashboardStats();
    }
  }, [TableReducer.pagination.page, TableReducer.pagination.limit, canRead, selectedUsers, selectedPeriod, startDate, endDate, filters]);

  const getCashbookRecords = () => {
    const user = getCahbookUser();
    const account = getSelectedAccount();
    const book = getSelectedBook();

    if (!user || !account) {
      toast.error('User or Account not found in storage');
      return;
    }

    setIsLoading(true);

    // Get date range based on selected period
    const dateRange = getDateRange(selectedPeriod);

    const json = {
      page: TableReducer.pagination.page,
      limit: TableReducer.pagination.limit,
      // Newest transaction date first, createdAt breaks ties
      sort: { date: -1, createdAt: -1 },
      search: {
        accountId: account?._id,
        bookId: book?._id,
        // Filter logic for different roles:
        // - SuperAdmin/Admin/Accountant with selected users: show only those users
        // - SuperAdmin/Admin/Accountant without selection: show all users
        // - Other roles: show only their own records
        ...((isSuperAdmin || isAdmin || isAccountant)
          ? (selectedUsers.length > 0
            ? { userId: { $in: selectedUsers.map(u => u._id) } }
            : {}) // Show all users
          : { userId: user?._id }), // Show only own records
        // Add date range filter
        ...(dateRange && {
          date: {
            $gte: dateRange.start,
            $lte: dateRange.end
          }
        }),
        // Add cashMethod filter
        ...(filters.cashMethod && { cashMethod: filters.cashMethod }),
        // Add paymentMethod filter
        ...(filters.paymentMethod && { paymentMethod: filters.paymentMethod })
      }
    };

    ApiHit(json, searchCashRecord)
      .then((res) => {
        if (res?.statusCode === 200) {
          dispatch(setApiJson(res?.data?.docs));
          dispatch(
            setPagination({
              total: res?.data?.totalDocs || 0,
              page: TableReducer.pagination.page,
              limit: TableReducer.pagination.limit,
            })
          );
        } else {
          toast.error(res?.error || 'Failed to fetch records');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error fetching cashbook records');
      })
      .finally(() => setIsLoading(false));
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = () => {
    const user = getCahbookUser();
    const account = getSelectedAccount();
    const book = getSelectedBook();

    setStatsLoading(true);

    // Get date range based on selected period
    const dateRange = getDateRange(selectedPeriod);

    const payload = {
      search: {
        accountId: account?._id,
        bookId: book?._id,
        // Filter logic for different roles:
        // - SuperAdmin/Admin/Accountant with selected users: show only those users
        // - SuperAdmin/Admin/Accountant without selection: show all users
        // - Other roles: show only their own records
        ...((isSuperAdmin || isAdmin || isAccountant)
          ? (selectedUsers.length > 0
            ? { userId: { $in: selectedUsers.map(u => u._id) } }
            : {}) // Show all users
          : { userId: user?._id }), // Show only own records
        // Add date range filter
        ...(dateRange && {
          date: {
            $gte: dateRange.start,
            $lte: dateRange.end
          }
        }),
        // Add cashMethod filter
        ...(filters.cashMethod && { cashMethod: filters.cashMethod }),
        // Add paymentMethod filter
        ...(filters.paymentMethod && { paymentMethod: filters.paymentMethod })
      },
    };

    console.log('📊 API Hit - fetchDashboardStats with payload:', JSON.stringify(payload, null, 2));
    ApiHit(payload, dailyStatsCashRecord)
      .then((res) => {
        console.log('Dashboard daily stats:', res);
        if (res?.data?.summary) {
          setStatsData({
            totalRecords: res.data.summary.totalRecords || 0,
            totalIn: res.data.summary.totalCashIn || 0,
            totalOut: res.data.summary.totalCashOut || 0,
            netBalance: res.data.summary.overallNetBalance || 0,
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching dashboard stats:', error);
      })
      .finally(() => setStatsLoading(false));
  };

  const baseIndex = (TableReducer.pagination.page - 1) * TableReducer.pagination.limit;

  const handleNavigate = (type) => {
    navigate(`/cashbook/${type}`);
  };

  const handleEdit = (row) => {
    if (!canEdit) return toast.error('You do not have permission to edit');
    navigate(`/cashbook/edit/${row?._id}`);
  };

  const handleDelete = (row) => {
    if (!canDelete) return toast.error('You do not have permission to delete');

    ApiHit({ _id: row?._id }, deleteCashRecord)
      .then((res) => {
        if (res?.statusCode === 200) {
          toast.success(res?.message || 'Deleted successfully');
          getCashbookRecords();
        } else {
          toast.error(res?.error || 'Failed to delete');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error deleting record');
      });
  };

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  const tableHeaders = [
    {
      title: 'S.No',
      key: 'serial',
      width: '60px',
      align: 'center',
      priority: 1,
      render: (_, __, index) => (
        <div className="text-center">{baseIndex + index + 1}</div>
      ),
    },
    {
      title: 'User Name',
      key: 'userId',
      priority: 1,
      render: (value) => (
        <div className="flex items-end md:items-start flex-col gap-1.5">
          <div className="text-sm font-semibold text-gray-900">{value?.name || 'N/A'}</div>
          <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 w-fit">
            {value?.roleId?.roleName || 'N/A'}
          </span>
        </div>
      ),
    },
    {
      title: 'Amount & Type',
      key: 'amountType',
      priority: 1,
      render: (_, row) => (
        <div className="flex flex-col gap-1.5">
          <div className={`text-base sm:text-sm font-bold ${row?.cashMethod === 'cashIn' ? 'text-green-700' : 'text-red-700'}`}>
            {row?.cashMethod === 'cashIn' ? '+' : '-'} {formatCurrency(row?.amount)}
          </div>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${row?.cashMethod === 'cashIn'
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
              }`}
          >
            {row?.cashMethod === 'cashIn' ? '↑ In' : '↓ Out'}
          </span>
        </div>
      ),
    },
    {
      title: 'Payment Method',
      key: 'paymentMethod',
      priority: 2,
      render: (_, row) => (
        <span className="text-sm font-medium text-gray-700">
          {row?.paymentMethod || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Description & Date',
      key: 'descriptionDate',
      priority: 2,
      render: (_, row) => (
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-gray-700 font-medium line-clamp-2 break-words max-w-[200px]">
            {row?.description || 'N/A'}
          </div>
          <div className="text-xs text-gray-500 font-medium">
            {formatDate(row?.date)}
          </div>
        </div>
      ),
    },
    {
      title: 'Proof',
      key: 'proofUrl',
      align: 'center',
      priority: 2,
      render: (value) => {
        const urls = Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : []);
        if (urls.length === 0)
          return <div className="text-gray-400 text-xs">No proof</div>;

        const firstUrl = urls[0];
        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(firstUrl);
        const isPDF = /\.pdf(\?|$)/i.test(firstUrl);

        const thumbnail = isImage ? (
          <img
            src={firstUrl}
            alt="proof"
            className="w-10 h-10 rounded-lg object-cover border-2 border-gray-200 group-hover:border-blue-400 transition-colors"
          />
        ) : isPDF ? (
          <div className="inline-flex items-center justify-center w-10 h-10 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors">
            <FileText className="w-5 h-5 text-red-500" />
          </div>
        ) : (
          <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
            <FileText className="w-5 h-5 text-gray-500" />
          </div>
        );

        // Single proof: open directly in new tab
        if (urls.length === 1) {
          return (
            <a
              href={firstUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex group"
              title="View proof"
            >
              {thumbnail}
            </a>
          );
        }

        // Multiple proofs: open gallery modal
        return (
          <button
            type="button"
            onClick={() => openGallery(urls)}
            className="inline-flex items-center gap-1.5 group"
            title={`View all ${urls.length} proofs`}
          >
            {thumbnail}
            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold group-hover:bg-blue-200 transition-colors">
              +{urls.length - 1}
            </span>
          </button>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      priority: 1,
      render: (_, row) => (
        <div className="flex justify-end md:justify-center sm:justify-center gap-1.5 sm:gap-2">
          {canEdit && (
            <ActionButton
              onClick={() => handleEdit(row)}
              tooltip="Edit Record"
              icon={<PencilIcon className="w-4 h-4" />}
            />
          )}
          {canDelete && (
            <DeleteActionButton
              onConfirm={() => handleDelete(row)}
              itemName="cash record"
              tooltip="Delete Record"
            />
          )}
        </div>
      ),
    },
  ];


  const handlePeriodChange = (option) => {
    setSelectedPeriod(option.value);

    // Enable date range mode if "Date Range" is selected
    if (option.value === 'daterange') {
      setDateRangeMode(true);
      setStartDate('');
      setEndDate('');
    } else {
      setDateRangeMode(false);
      setStartDate('');
      setEndDate('');
    }
  };

  const handleDateRangeSearch = (start, end) => {
    console.log('📅 Date range search:', start, 'to', end);
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearSearch = () => {
    console.log('🧹 Clearing date range search');
    setStartDate('');
    setEndDate('');
  };

  const handleFilterChange = (newFilters) => {
    console.log('🎯 Filters changed:', newFilters);
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    console.log('🧹 Clearing all filters');
    setFilters({
      cashMethod: '',
      paymentMethod: ''
    });
  };

  const handleExportData = async (format) => {
    try {
      toast.loading('Preparing export...');

      const user = getCahbookUser();
      const account = getSelectedAccount();
      const book = getSelectedBook();

      if (!user || !account) {
        toast.dismiss();
        toast.error('User or Account not found in storage');
        return;
      }

      // Get date range based on selected period
      const dateRange = getDateRange(selectedPeriod);

      const json = {
        page: 1,
        limit: 1000, // Export limit of 1000 records
        // Keep export in the same date-wise order as the table
        sort: { date: -1, createdAt: -1 },
        search: {
          accountId: account?._id,
          bookId: book?._id,
          // Filter logic for different roles
          ...((isSuperAdmin || isAdmin || isAccountant)
            ? (selectedUsers.length > 0
              ? { userId: { $in: selectedUsers.map(u => u._id) } }
              : {})
            : { userId: user?._id }),
          // Add date range filter
          ...(dateRange && {
            date: {
              $gte: dateRange.start,
              $lte: dateRange.end
            }
          }),
          // Add cashMethod filter
          ...(filters.cashMethod && { cashMethod: filters.cashMethod }),
          // Add paymentMethod filter
          ...(filters.paymentMethod && { paymentMethod: filters.paymentMethod })
        }
      };

      const res = await ApiHit(json, searchCashRecord);

      if (res?.statusCode === 200 && res?.data?.docs) {
        const allData = res.data.docs;

        if (allData.length === 0) {
          toast.dismiss();
          toast.error('No data to export');
          return;
        }

        // Define export headers
        const exportHeaders = [
          { title: 'S.No', key: 'serialNumber' },
          { title: 'User Name', key: 'userName' },
          { title: 'User Role', key: 'userRole' },
          { title: 'Amount', key: 'amount' },
          { title: 'Cash Type', key: 'cashType' },
          { title: 'Payment Method', key: 'paymentMethod' },
          { title: 'Description', key: 'description' },
          { title: 'Date', key: 'date' },
          { title: 'Proof', key: 'proof' }
        ];

        // Flatten and format data for export
        const flattenedData = allData.map((row, index) => ({
          serialNumber: index + 1,
          userName: row.userId?.name || 'N/A',
          userRole: row.userId?.roleId?.roleName || 'N/A',
          amount: row.amount || 0,
          cashType: row.cashMethod === 'cashIn' ? 'Cash In' : 'Cash Out',
          paymentMethod: row.paymentMethod || 'N/A',
          description: row.description || 'N/A',
          date: formatDate(row.date),
          proof: (() => {
            const urls = Array.isArray(row.proofUrl) ? row.proofUrl.filter(Boolean) : (row.proofUrl ? [row.proofUrl] : []);
            return urls.length === 0 ? 'No proof' : (urls.length === 1 ? 'Available' : `Available (${urls.length})`);
          })()
        }));

        const filename = `cashbook_export_${new Date().toISOString().split('T')[0]}`;

        toast.dismiss();

        if (format === 'csv') {
          exportToCSV(flattenedData, exportHeaders, `${filename}.csv`);
          toast.success(`Exported ${allData.length} records to CSV`);
        } else if (format === 'pdf') {
          exportToPDF(flattenedData, exportHeaders, `${filename}.pdf`, 'Cashbook Report');
          toast.success(`Exported ${allData.length} records to PDF`);
        }
      } else {
        toast.dismiss();
        toast.error('Failed to fetch data for export');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Error exporting data');
      console.error('Export error:', error);
    }
  };

  const filteredHeaders = canEdit || canDelete
    ? tableHeaders
    : tableHeaders.filter((h) => h.key !== 'actions');

  if (!canRead) {
    return (
      <div>
        <PageHeader
          title="Cashbook Management"
          description="Track, view, and manage all business cash transactions efficiently"
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
            <p className="text-gray-600">You don’t have permission to view cashbook records.</p>
          </div>
        </div>
      </div>
    );
  }

  // Stats cards data
  const { totalRecords, totalIn, totalOut, netBalance } = statsData;

  const stats = [
    {
      id: 1,
      title: 'Total Records',
      value: statsLoading ? 'Loading...' : totalRecords.toLocaleString('en-IN'),
      icon: <BarChart3 className="w-6 h-6 text-blue-500" />,
      color: 'bg-blue-200',
    },
    {
      id: 2,
      title: 'Total Cash In',
      value: statsLoading ? 'Loading...' : `₹${totalIn.toLocaleString('en-IN')}`,
      icon: <TrendingUp className="w-6 h-6 text-green-600" />,
      color: 'bg-green-200',
    },
    {
      id: 3,
      title: 'Total Cash Out',
      value: statsLoading ? 'Loading...' : `₹${totalOut.toLocaleString('en-IN')}`,
      icon: <TrendingDown className="w-6 h-6 text-red-600" />,
      color: 'bg-red-200',
    },
    {
      id: 4,
      title: 'Net Balance',
      value: statsLoading ? 'Loading...' : `₹${netBalance.toLocaleString('en-IN')}`,
      icon: <IndianRupee className="w-6 h-6 text-purple-600" />,
      color: 'bg-purple-200',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Cashbook Management"
        description="Track, view, and manage all business cash transactions efficiently"
      />

      {/* User MultiSelect for SuperAdmin, Admin, and Accountant */}
      {(isSuperAdmin || isAdmin || isAccountant) && (
        <UserMultiSelect
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
        />
      )}

      {/* Filter Section */}
      <div className="mt-2">
        <FilterSection
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          paymentMethods={paymentMethods}
        />
      </div>

      {/* Dashboard Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mt-4 sm:mt-6">
        {stats.map((item) => (
          <div
            key={item.id}
            className={`${item.color} rounded-xl sm:rounded-2xl shadow-sm p-3 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:shadow-md transition-all duration-200 active:scale-95`}
          >
            <div className="flex-1 w-full">
              <p className="text-xs sm:text-sm text-gray-600 font-medium">{item.title}</p>
              <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5 sm:mt-1 truncate">{item.value}</h3>
            </div>
            <div className="p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl shadow-sm mt-2 sm:mt-0 self-end sm:self-auto">{item.icon}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 sm:mt-5">
        {canAdd && (
          <div>
            <div className='flex flex-col md:flex-row md:items-center md:justify-between'>
              <div className='flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mb-4'>
                <select
                  className="h-10 sm:h-9 text-sm px-3 py-2 bg-white border rounded-lg transition-colors border-slate-300 hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20 font-medium"
                  value={selectedPeriod}
                  onChange={(e) => {
                    console.log('🎯 Select onChange triggered with:', e.target.value);
                    handlePeriodChange({ value: e.target.value });
                  }}
                >
                  {dashboardOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {(isLoading || statsLoading) && (
                  <span className="text-xs sm:text-sm text-blue-600 font-medium animate-pulse">
                    Loading data...
                  </span>
                )}
              </div>
              <div className="mb-3 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2">
                {canEdit &&
                  <Button
                    className="bg-green-200 hover:bg-green-500 text-green-700 hover:text-white font-semibold active:scale-95 transition-all"
                    onClick={() => handleNavigate('cashIn')}
                  >
                    <span className="mr-2">Cash In</span> <CirclePlus size={18} />
                  </Button>
                }
                <Button
                  className="bg-red-200 hover:bg-red-500 text-red-700 hover:text-white font-semibold active:scale-95 transition-all"
                  onClick={() => handleNavigate('cashOut')}
                >
                  <span className="mr-2">Cash Out</span> <CircleMinus size={18} />
                </Button>

              </div>
            </div>
            {dateRangeMode && (
              <div className="mb-6 bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <CalendarRange className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700">Select Date Range</h3>
                </div>
                <DateSearch
                  onSearch={handleDateRangeSearch}
                  onClear={handleClearSearch}
                />
              </div>
            )}
          </div>
        )}

        <AppTable
          TH={filteredHeaders}
          TD={ApiReducer.apiJson || []}
          emptyMessage="No record found"
          hidePagination={false}
          isLoading={isLoading}
          enableExport={isSuperAdmin || isAdmin}
          onExportData={handleExportData}
          exportFileName="cashbook_export"
        />
      </div>

      <ProofGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        urls={galleryUrls}
      />
    </div>
  );
}

export default CashbookManagement;
